import { CommonModule } from '@angular/common';
import { Component, Input, QueryList, ViewChildren, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { ConfigService } from '../../shared-services/config.service';
import { HttpClient } from '@angular/common/http';
import { GlobalDropdownComponent } from '../global-dropdown/global-dropdown.component';
import { MatSliderComponent } from '../mat-slider/mat-slider.component';
import {
  debounceTime,
  distinctUntilChanged,
  elementAt,
  OperatorFunction,
  Subscription,
  startWith,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent } from '../button/button.component';
// import saveAs from 'file-saver';
import { SearchContainerComponent } from '../search-container/search-container.component';

@Component({
  selector: 'app-global-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    GlobalDropdownComponent,
    MatSliderComponent,
    MatIcon,
    ButtonComponent,
    SearchContainerComponent
  ],
  templateUrl: './global-filter.component.html',
  styleUrl: './global-filter.component.scss',
})
export class GlobalFilterComponent implements OnChanges, OnDestroy {
  @Input() config!: any;
  alignRight: boolean = false;
  filters!: any[];
  options: any = {};
  filterForm: FormGroup = new FormGroup({});
  selectedKey: string = '';
  showResetButton = false;
  private defaultAppliedKeys = new Set<string>();
  private manualSelection: Set<string> = new Set();
  private autoPatchedKeys = new Set<string>();
  private resetStateSubscription?: Subscription;
  @ViewChildren(MatSliderComponent) sliders!: QueryList<MatSliderComponent>;
  showRationalizeSection = false;
  submittedItemForRationalize: string | null = null;
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    const { apiUrl } = this.config;
    this.filters = this.config?.content || [];
    this.showResetButton = false; // Disabled as per user request
    const queryParams = this.route?.snapshot?.queryParams;

    this.filters.forEach((item: any) => {
      const initialValue = item?.initialValue || null;
      this.filterForm.addControl(item?.key, new FormControl(initialValue));
      console.log('[GlobalFilter] Created form control for', item?.key, 'with initialValue:', initialValue);
    });

    this.resetStateSubscription = this.globalFilterService.resetState$.subscribe(() => {
      this.applyLocalResetState();
    });
    // Detect if we are on the quote-parts screen and align filters to the right
    try {
      const url = this.router?.url || window.location.pathname || '';
      if (typeof url === 'string' && url.includes('quote-parts')) {
        this.alignRight = true;
        console.log('[GlobalFilter] alignRight enabled for route:', url);
      }
    } catch (e) {
      // ignore
    }

    //  Pre-fetch all static filters and apply defaults
    this.filters.forEach((filter, index) => {
      if (filter?.options === 'data' && filter?.apiUrl?.startsWith('assets/')) {
        this.http.get(filter?.apiUrl).subscribe((staticRes: any) => {
          const options = staticRes?.data || [];
          this.filters[index].optionsList = options;
          this.options[filter?.key] = options;

          const control = this.filterForm.get(filter?.key);
          const hasQueryParam = !!queryParams?.[filter?.key];
          const currentVal = control?.value;
          const isEmptyValue =
            currentVal === null ||
            (Array.isArray(currentVal) && currentVal.length === 0);

          const shouldApplyDefault =
            !hasQueryParam &&
            isEmptyValue &&
            options.length > 0 &&
            !this.defaultAppliedKeys.has(filter.key) &&
            filter?.isfirstOptionDefault;

          if (shouldApplyDefault) {
            const defaultValue = filter.isMultiSelect
              ? [options[0]]
              : options[0];
            control?.setValue(defaultValue, { emitEvent: false });
            this.defaultAppliedKeys.add(filter?.key);
          }
        });
      } else if (filter?.apiUrl && !filter?.apiUrl?.startsWith('assets/')) {
        const method = (filter?.method || 'POST').toUpperCase();
        const payload = filter?.payload || {};
        const request = method === 'POST' ? this.http.post(filter.apiUrl, payload) : this.http.get(filter.apiUrl);
        console.log('[GlobalFilter] fetching filter options', { key: filter?.key, method, url: filter?.apiUrl, payload });
        request.subscribe({
          next: (res: any) => {
            const options = res?.data || res || [];
            this.filters[index].optionsList = options;
            this.options[filter?.key] = options;
          },
          error: (err: any) => {
            console.error('[GlobalFilter] Error fetching filter', filter?.key, { url: filter?.apiUrl, method, payload, err });
          }
        });
      }
    });

    // Listen to valueChanges AFTER local dropdowns are pre-loaded
    let valueChange$ = this.filterForm.valueChanges.pipe(
      debounceTime(0),
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
      )
    );

    //  handling for customer_spec change on manual selection
    this.filterForm.get('customer_spec')?.valueChanges.subscribe((val) => {
      if (val !== null && val !== undefined && val !== '') {
        //  this.manualSelection.add('customer_spec');
        this.autoPatchedKeys.delete('customer_spec');
      }
    });
    //  handling for customer_name change on manual selection
    this.filterForm.get('customer_name')?.valueChanges.subscribe((val) => {
      if (val !== null && val !== undefined && val !== '') {
        this.autoPatchedKeys.delete('customer_name');
      }
    });

    // Handle dependent filters (e.g., productNumber depends on productName)
    this.filters.forEach((filter) => {
      if (filter?.dependsOn) {
        console.log('[GlobalFilter] Setting up dependent filter listener:', {
          dependentKey: filter.key,
          parentKey: filter.dependsOn,
          apiUrl: filter.apiUrl,
          payload: filter.payload,
        });

        this.filterForm.get(filter.dependsOn)?.valueChanges.subscribe((val) => {
          console.log('[GlobalFilter] parent filter changed:', {
            parentKey: filter.dependsOn,
            parentValue: val,
            dependentKey: filter.key,
          });
          if (val !== null && val !== undefined && (Array.isArray(val) ? val.length > 0 : true)) {
            this.reloadDependentFilter(filter);
          }
        });
      }
    });

    // If no queryParams, auto-trigger with startWith
    if (!Object.keys(queryParams)?.length) {
      valueChange$ = this.filterForm.valueChanges.pipe(
        startWith(this.filterForm.value),
        debounceTime(0),
      );
    }
    valueChange$
      .subscribe((val) => {
        const payload = {
          filter: {
            where: this.getWhereCondition(),
            join: {},
            orderBy: {},
          },
        };
        if (apiUrl) {
          this.configService
            .getDropdownOptions(apiUrl, payload)
            .subscribe((res) => {
              this.filters = this.filters.map((filter: any) => {
                if (!filter || !filter.key) {
                  return filter;
                }
                if (res.hasOwnProperty(filter.key)) {
                  const options = res[filter.key];

                  const control = this.filterForm.get(filter.key);
                  const hasQueryParam = !!queryParams?.[filter.key];
                  const currentVal = control?.value;

                  const isEmptyValue =
                    currentVal === null ||
                    (Array.isArray(currentVal) && currentVal?.length === 0);

                  if (options?.length > 0 && !!filter?.isfirstOptionDefault) {
                    if (isEmptyValue || this.autoPatchedKeys.has(filter?.key)) {
                      const defaultValue = filter.isMultiSelect ? [options[0]] : options[0];
                      control?.setValue(defaultValue, { emitEvent: false });
                      const selectionValues = this.toFilterSelectionValues(defaultValue, !!filter?.isMultiSelect);

                      this.globalFilterService.updateFilterSelection(
                        filter?.key,
                        selectionValues,
                        this.filters?.find((elm: any) => elm.key === filter?.key)?.isQueryParam || false
                      );
                      this.autoPatchedKeys.add(filter?.key);
                    }

                    //  added logic for update patching value on select all 

                    if (filter?.key === 'customer_name') {
                      const selectedSpecs = this.filterForm.get('customer_spec')?.value || [];
                      const allSpecs = this.options['customer_spec'] || []; // all available specs

                      // If user selected more than one spec BUT not ALL specs
                      if (
                        Array.isArray(selectedSpecs) &&
                        selectedSpecs?.length > 0 && // more than one
                        selectedSpecs?.length < allSpecs.length // not ALL
                      ) {
                        const patchValue = [...options]; // select ALL available customer names
                        control?.setValue(patchValue, { emitEvent: false });
                        const selectionValues = this.toFilterSelectionValues(patchValue, !!filter?.isMultiSelect);

                        this.globalFilterService.updateFilterSelection(
                          filter.key,
                          selectionValues,
                          filter?.isQueryParam || false
                        );

                        this.autoPatchedKeys.add(filter.key);
                      }
                    }



                    if (options?.length && !this.autoPatchedKeys.has(filter?.key) && !!queryParams?.[filter?.key]) {
                      const defaultValue = filter?.isMultiSelect ? [options[0]] : options[0];
                      // control?.setValue(defaultValue, { emitEvent: false });

                      this.globalFilterService.updateFilterSelection(
                        filter.key,
                        this.toFilterSelectionValues(currentVal, !!filter?.isMultiSelect),
                        this.filters.find((elm: any) => elm.key === filter.key)
                          ?.isQueryParam || false
                      );

                      this.autoPatchedKeys.add('customer_spec');
                    }
                  }



                  return {
                    ...filter,
                    optionsList: options,
                  };
                }
                return filter;
              });
              if (!!res) {
                Object.entries(res)?.map(([key, value]) => {
                  if (this.globalFilterService.activeFilter !== key) {
                    this.options[key] = value;
                  }
                });
              }

              this.globalFilterService.dropdownOptions = this.options;

              // added logic to update the  customer name list as per customer spec selection 

              // const latestCustomerNames = this.globalFilterService.dropdownOptions['customer_name'] || [];
              // const currentCustomerNames = this.filterForm.get('customer_name')?.value || [];

              // const validSelection = Array.isArray(currentCustomerNames)
              //   ? currentCustomerNames.filter((name: string) =>
              //     latestCustomerNames.includes(name)
              //   )
              //   : latestCustomerNames.includes(currentCustomerNames)
              //     ? currentCustomerNames
              //     : null;

              // if (JSON.stringify(validSelection) !== JSON.stringify(currentCustomerNames)) {
              //   this.filterForm.get('customer_name')?.setValue(validSelection, { emitEvent: false });
              //   this.globalFilterService.updateFilterSelection(
              //     'customer_name',
              //     validSelection,
              //     this.filters?.find(elm => elm.key === 'customer_name')?.isQueryParam || false
              //   );

              // }
            });
        }
      });

    //  Handle query param
    const paramKeyToFormKeyMap: { [key: string]: string } = {
      customer_spec: 'customer_spec',
      customer_name: 'customer_name'
    };

    if (!!Object.keys(queryParams)?.length) {
      Object.keys(queryParams).forEach((paramKey) => {
        const formKey = paramKeyToFormKeyMap[paramKey];
        const rawValue = queryParams[paramKey];

        if (formKey && this.filterForm.contains(formKey)) {
          const isMultiSelect = this.config.content.find(
            (f: any) => f.key === formKey
          )?.isMultiSelect;

          const valueArray: any = Array.isArray(rawValue)
            ? rawValue.map((v: string) => decodeURIComponent(v))
            : isMultiSelect
              ? [decodeURIComponent(rawValue)]
              : decodeURIComponent(rawValue);

          const patchValue = valueArray;

          this.filterForm.patchValue(
            {
              [formKey]: patchValue,
            },
            { emitEvent: true }
          );
          // this.globalFilterService.activeFilter = formKey;
          this.globalFilterService.updateFilterSelection(
            formKey,
            this.toFilterSelectionValues(patchValue, !!isMultiSelect),
            this.filters?.find((elm: any) => elm.key === formKey)?.isQueryParam ||
            false
          );
        }
      });
    } else {
      setTimeout(() => {
        this.globalFilterService.initialTrigger();
      }, 200);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      // Config changed, update form controls with initial values
      const updatedFilters = this.config?.content || [];
      updatedFilters.forEach((filter: any) => {
        const control = this.filterForm.get(filter.key);
        if (control && filter.initialValue) {
          console.log('[GlobalFilter] Patching form control', filter.key, 'with initialValue:', filter.initialValue);
          control.setValue(filter.initialValue, { emitEvent: true });
        }
      });
    }
  }

  // helper function to check query params 
  private isQueryParamValue(key: string, value: any): boolean {
    const paramValue = this.route.snapshot.queryParamMap.get(key);
    return paramValue !== null && paramValue == value;
  }

  private getWhereCondition(): any[] {
    const currentPath = this.router.url.split('?')[0]; // Remove query params
    const dashboardName = currentPath.startsWith('/')
      ? currentPath.substring(1)
      : currentPath;

    const isInitialLoad = Object.values(this.filterForm.value).every(
      (v) => v === null
    );

    if (dashboardName === 'europe-impact-report' && isInitialLoad) {
      return [];
    }
    let staticFilters: any[] = [];
    const customerNameValue = this.filterForm.value['customer_name'];
    // Apply filters based on dashboard
    if (dashboardName === 'customer-overview') {
      // added condition to filter out default customername in payload if customer name is selected 
      if (!customerNameValue || (Array.isArray(customerNameValue) && customerNameValue?.length === 0)) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
        ];
      }
    }
    else if (dashboardName === 'cspec-sales-bridge') {
      if (!customerNameValue && (!Array.isArray(customerNameValue) || customerNameValue.length > 0)) {
        staticFilters = [

          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
        ];
      }
    }
    else if (dashboardName !== 'opportunity-overview' && dashboardName !== 'cspec-sales-bridge' && dashboardName !== 'europe-impact-report') {
      if (!customerNameValue || (Array.isArray(customerNameValue) && customerNameValue?.length === 0)) {
        staticFilters = [
          {
            attributeName: 'customer_name',
            operator: '!=',
            attributeValues: ['NA'],
            logicalOperator: 'AND',
          },
          {
            attributeName: 'latest_shop_order_flag',
            operator: '=',
            attributeValues: [true],
            logicalOperator: 'AND',
          },
        ];
      }
    }

    // Dynamic form filters
    const dynamicFilters = this.config?.content
      .filter((elm: any) => {
        const val = this.filterForm.value[elm?.key];
        if ((elm?.key === 'customer_spec' || elm?.key === 'customer_name') && this.isQueryParamValue(elm?.key, val)) {
          return false; // skip in payload  only if the value came from query params and retains the full option list 
        }

        return (val !== null && val !== undefined && !elm?.isQueryParam && !this.autoPatchedKeys.has(elm?.key));
      })
      .map((elm: any) => {
        const rawVal = this.filterForm.value[elm.key];
        const values = Array.isArray(rawVal)
          ? rawVal.filter((v: any) => v !== null)
          : [rawVal];

        // Skip filters with empty values
        if (!values?.length || values.every((v) => v === null || v === '')) {
          return null;
        }

        return {
          attributeName: elm?.key,
          operator: values?.length > 1 ? 'IN' : '=',
          attributeValues: values,
          logicalOperator: 'AND',
        };
      })
      .filter(Boolean); // Remove nulls from the result

    // Handle logicalOperator chaining
    if (dynamicFilters.length > 0) {
      dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
      if (staticFilters.length > 0)
        staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
    } else {
      if (staticFilters.length > 0)
        staticFilters[staticFilters.length - 1].logicalOperator = '';
    }

    return [...staticFilters, ...dynamicFilters];
  }

  resetButton() {
    this.globalFilterService.resetDashboardState();

    //  API call again to refresh dropdowns list ui on reset
    if (!this.config?.apiUrl) {
      return;
    }

    const payload = {
      filter: {
        where: this.getWhereCondition(),
        join: {},
        orderBy: {},
      },
    };

    this.configService
      .getDropdownOptions(this.config.apiUrl, payload)
      .subscribe((res) => {
        this.filters = this.filters.map((filter: any) => {
          if (res.hasOwnProperty(filter.key)) {
            return {
              ...filter,
              optionsList: res[filter.key], //refresh UI dropdown list
            };
          }
          return filter;
        });
        this.options = { ...this.options, ...res };
        this.globalFilterService.dropdownOptions = this.options;
      });
  }

  private applyLocalResetState(): void {
    this.filterForm.reset();
    this.selectedKey = '';
    this.autoPatchedKeys.clear();
    this.manualSelection.clear();
    this.sliders?.forEach((slider) => slider.reset());
  }

  onSearch(searchTerm: string, key: string): void {
    const valueToSet = searchTerm || '';
    this.filterForm.get(key)?.setValue(valueToSet, { emitEvent: false });
    this.globalFilterService.updateFilterSelection(
      key,
      this.toFilterSelectionValues(valueToSet, false),
      false
    );
  }

  onButtonSelectionChange(key: string) {
    this.selectedKey = key;
    if (key === 'addButton') {
      this.handleAddButton();
    }
  }

  private handleAddButton() {

    const ItemName = this.filterForm.get('itemName')?.value;
    const BundleId = this.filterForm.get('BundleId')?.value;
    const customer_name = this.filterForm.get('customer_name')?.value;
    const region = this.filterForm.get('region')?.value;
    const organization = this.filterForm.get('organization')?.value;
    console.log('sspk ItemName', ItemName);
    console.log('sspk BundleId', BundleId);
    console.log('sspk customer_name', customer_name);
    console.log('sspk region', region);
    console.log('sspk organization', organization);

    const addButtonFilter = this.filters.find((f: any) => f.key === 'addButton');
    console.log('sspk addButtonFilter', addButtonFilter);
    const apiUrl = addButtonFilter?.apiUrl;
    const apiUrl2 = addButtonFilter?.apiUrl2;
    const apiUrl3 = addButtonFilter?.apiUrl3;
    console.log('sspk apiUrl', apiUrl);
    console.log('sspk apiUrl2', apiUrl2);
    console.log('sspk apiUrl3', apiUrl3);

    if (!apiUrl) {
      console.error('sspk Add button API URL not configured');
      return;
    }

    const payload: any = {};
    const payload2: any = {};
    const payload3: any = {};
    const customerValues = this.toFilterSelectionValues(customer_name, true);
    const regionValues = this.toFilterSelectionValues(region, true);
    const organizationValues = this.toFilterSelectionValues(organization, true);

    if (customerValues.length > 0 || regionValues.length > 0 || organizationValues.length > 0) {
      if (customerValues.length > 0) payload3.customer_name = customerValues;
      if (regionValues.length > 0) payload3.region = regionValues;
      if (organizationValues.length > 0) payload3.organization = organizationValues;

      this.http.post(apiUrl, payload3).subscribe({
        next: (response: any) => {
          console.log('sspk Add API response:', response);
          // Assuming response is an array of data
          const newData = response?.data || response || [];
          // const currentData = this.globalFilterService.getTableData();
          this.globalFilterService.setChartData(newData);
        },
        error: (error) => {
          console.error('sspk Error calling add API:', error);
        }
      });
    }
    const itemValues = this.toFilterSelectionValues(ItemName, true);
    if (itemValues.length > 0) {
      payload['item_id'] = [itemValues];
      this.http.post(apiUrl, payload).subscribe({
        next: (response: any) => {
          console.log('sspk Add API response:', response);
          // Assuming response is an array of data
          const newData = this.enrichQuotePartsRows(response?.data || response || []);
          const currentData = this.globalFilterService.getTableData();
          this.globalFilterService.setTableData([...currentData, ...newData]);
        },
        error: (error) => {
          console.error('sspk Error calling add API:', error);
        }
      });
    }
    const bundleValues = this.toFilterSelectionValues(BundleId, true);
    if (bundleValues.length > 0) {
      if (!apiUrl3) {
        console.error('sspk Bundle item IDs API URL not configured');
        return;
      }

      payload2['bundle_id'] = [bundleValues];
      this.http.post(apiUrl3, payload2).subscribe({
        next: (response: any) => {
          const items = response?.data || response || [];
          const itemIds: string[] = (Array.isArray(items) ? items : [])
            .map((row: any) => row?.item_id)
            .filter((value: any) => typeof value === 'string' && value.trim().length > 0);

          if (!itemIds.length) {
            return;
          }

          const payloadForSecondApi = {
            item_id: [itemIds]
          };

          this.http.post(apiUrl, payloadForSecondApi).subscribe({
            next: (resp2: any) => {
              const newData2 = this.enrichQuotePartsRows(resp2?.data || resp2 || []);
              const currentData = this.globalFilterService.getTableData();
              console.log('Current Data before update:', currentData);
              this.globalFilterService.setTableData([...currentData, ...newData2]);
            },
            error: (err) => console.error('Error calling second API:', err)
          });
        },
        error: (error) => {
          console.error('sspk Error calling add API:', error);
        }
      });
    }

    console.log('sspk Calling add API with payload:', payload);
    console.log('sspk Calling add API with payload2:', payload2);


    // this.http.post(apiUrl, payload).subscribe({
    //   next: (response: any) => {
    //     console.log('sspk Add API response:', response);
    //     // Assuming response is an array of data
    //     const tableData = response?.data || response || [];
    //     this.globalFilterService.setTableData(tableData);
    //   },
    //   error: (error) => {
    //     console.error('sspk Error calling add API:', error);
    //   }
    // });
  }

  private enrichQuotePartsRows(rows: any[]): any[] {
    const currentRoute = this.router?.url || '';
    const isQuotePartsRoute = currentRoute.includes('quote-parts');
    if (!isQuotePartsRoute || !Array.isArray(rows)) {
      return Array.isArray(rows) ? rows : [];
    }

    return rows.map((row: any) => {
      if (!row || typeof row !== 'object') {
        return row;
      }

      return {
        ...row,
        discount_type: row.discount_type ?? null,
        line_discount: row.line_discount ?? null,
      };
    });
  }

  private reloadDependentFilter(filter: any): void {
    console.log('[GlobalFilter] reloadDependentFilter called for:', {
      filterKey: filter.key,
      dependsOn: filter.dependsOn,
      hasApiUrl: !!filter?.apiUrl,
      apiUrl: filter?.apiUrl,
    });

    if (!filter?.apiUrl || !filter?.dependsOn) {
      console.log('[GlobalFilter] reloadDependentFilter - missing apiUrl or dependsOn, returning');
      return;
    }

    const dependsOnValue = this.filterForm.get(filter.dependsOn)?.value;
    console.log('[GlobalFilter] reloadDependentFilter - dependsOnValue:', {
      key: filter.dependsOn,
      value: dependsOnValue,
      type: typeof dependsOnValue,
      isArray: Array.isArray(dependsOnValue),
    });

    if (!dependsOnValue || (Array.isArray(dependsOnValue) && dependsOnValue.length === 0)) {
      console.log('[GlobalFilter] reloadDependentFilter - no dependent value, clearing options');
      this.options[filter.key] = [];
      return;
    }

    // Interpolate the dependent value into the payload
    const payload = this.interpolatePayload(
      filter.payload || {},
      filter.dependsOn,
      dependsOnValue
    );

    const method = (filter?.method || 'POST').toUpperCase();
    const request =
      method === 'POST'
        ? this.http.post(filter.apiUrl, payload)
        : this.http.get(filter.apiUrl);

    console.log('[GlobalFilter] reloading dependent filter', {
      key: filter?.key,
      dependsOn: filter?.dependsOn,
      method,
      url: filter?.apiUrl,
      payload,
    });

    request.subscribe({
      next: (res: any) => {
        const options = res?.data || res || [];
        console.log('[GlobalFilter] API response for dependent filter:', {
          key: filter?.key,
          responseData: options,
          optionsCount: options.length,
        });

        const filterIndex = this.filters.findIndex((f) => f.key === filter.key);
        if (filterIndex >= 0) {
          this.filters[filterIndex].optionsList = options;
          console.log('[GlobalFilter] Updated filter optionsList at index', {
            filterIndex,
            filterKey: filter.key,
            newOptions: options,
          });
        }
        this.options[filter.key] = options;
        // Clear dependent filter value when parent changes
        this.filterForm.get(filter.key)?.setValue(null, { emitEvent: false });
        console.log('[GlobalFilter] dependent filter reloaded', {
          key: filter?.key,
          optionsCount: options.length,
          filterIndex,
        });
      },
      error: (err: any) => {
        console.error('[GlobalFilter] Error reloading dependent filter', filter?.key, {
          url: filter?.apiUrl,
          method,
          payload,
          err,
        });
      },
    });
  }

  private interpolatePayload(payload: any, dependsOnKey: string, value: any): any {
    const interpolated = JSON.parse(JSON.stringify(payload)); // Deep clone
    const placeholder = `{${dependsOnKey}}`;
    const actualValue = Array.isArray(value) ? value[0] : value;

    console.log('[GlobalFilter] interpolatePayload', {
      dependsOnKey,
      placeholder,
      actualValue,
      originalPayload: payload,
    });

    const replaceInValue = (val: any): any => {
      if (typeof val === 'string') {
        return val.replace(placeholder, actualValue);
      } else if (Array.isArray(val)) {
        return val.map(replaceInValue);
      } else if (val && typeof val === 'object') {
        const result: any = {};
        for (const key in val) {
          result[key] = replaceInValue(val[key]);
        }
        return result;
      }
      return val;
    };

    const result = replaceInValue(interpolated);

    console.log('[GlobalFilter] interpolated payload result:', result);
    return result;
  }

  private toFilterSelectionValues(value: any, isMultiSelect: boolean): any[] {
    const normalizedValues = (Array.isArray(value) ? value.flat(Infinity) : [value])
      .map((item: any) => (typeof item === 'string' ? item.trim() : item))
      .filter((item: any) => item !== null && item !== undefined && item !== '');

    if (isMultiSelect) {
      return normalizedValues;
    }

    return normalizedValues.length > 0 ? [normalizedValues[0]] : [];
  }

  download(url: string) {
    const payload = {
      filter: {
        where: this.getWhereCondition(),
        join: {},
        orderBy: {},
      },
    };
    this.globalFilterService.downloadExcel(url, payload).subscribe({
      next: (blob: Blob) => {
        const currentPath = this.router.url.split('?')[0];
        const dashboardName = currentPath.startsWith('/')
          ? currentPath.substring(1)
          : currentPath;
        let fileName = 'download.xlsx';
        if (dashboardName === 'europe-impact-report') {
          fileName = 'Europe Impact Report.xlsx';
        } else if (dashboardName === 'customer-overview') {
          fileName = 'customerOverview.xlsx';
        }
        // saveAs(blob, fileName);
      },
      error: (err) => {
        console.error('Download failed', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.resetStateSubscription?.unsubscribe();
  }
}
